import { Dispatch, SetStateAction, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useGetPortCatalogMutation } from 'app/api/marine-infrastructure';
import { throttle } from 'app/helpers/throttle';
import { t } from 'i18next';
import { PortCatalogData } from 'ais-facade-api/api';
import { InfoWrapper } from 'app/components';
import { SearchStateI } from 'app/hooks/useFilterComboboxState';
import { FormikErrors } from 'formik';
import { FilterFormData } from './FilterPanel';
import { useDebounce } from 'app/hooks/useDebounce';
import { AutoComplete, SelectedOptionI } from '../AutoComplete';
import { useIntersectionObserver } from '@siberiacancode/reactuse';
import styles from './FilterPanel.module.scss';

type PortFieldT = 'inPortKey' | 'destinationPortKey' | 'port';
interface PortInFieldI {
  fieldName: PortFieldT;
  searchState?: [SearchStateI, Dispatch<SetStateAction<SearchStateI>>];
  onSelect: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<FilterFormData>>;
}
export const PortInField = ({ fieldName, searchState: searchStateArray, onSelect }: PortInFieldI) => {
  const [getCatalog, { data: portsData, ...catalogRequest }] = useGetPortCatalogMutation({
    fixedCacheKey: 'get-ports-catalog-mutation',
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);

  const getPorts = (term: string, offset: number) => {
    if (term.length < 2) {
      debouncedGetCatalogHandler({
        filter: {},
        order: {
          direction: 'ASC',
          by: 'CITY_NAME',
        },
        pagination: {
          offset: offset,
          limit: 10,
        },
      });
    }

    if (term.length >= 2) {
      debouncedGetCatalogHandler({
        filter: {
          term: term,
        },
        order: {
          direction: 'ASC',
          by: 'CITY_NAME',
        },
        pagination: {
          offset: offset,
          limit: 10,
        },
      });
    }
  };

  const [offset, setOffset] = useState<number>(0);
  const [ports, setPorts] = useState<{ ports: PortCatalogData[] }>({ ports: [] });
  const [searchState, setSearchState] = searchStateArray || [];
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState(false);
  const debouncedValue = useDebounce<string>(inputValue, 500);
  const [options, setOptions] = useState<
    | {
        value?: string;
        label?: string | JSX.Element;
        placeholder?: string;
      }[]
    | null
  >(null);

  const initialPlaceholderValue = useMemo(() => {
    if (fieldName === 'destinationPortKey' || fieldName === 'inPortKey') {
      return searchState?.term[fieldName];
    }
    return '';
  }, [searchState, fieldName]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (inputRef.current?.value) {
      getPorts(inputRef.current?.value, 0);
    }

    if (initialPlaceholderValue && catalogRequest.isUninitialized) {
      setOffset(0);
      setPorts({ ports: [] });
      getPorts(initialPlaceholderValue, 0);
    }
    if (!initialPlaceholderValue && !debouncedValue) {
      setPorts({ ports: [] });

      getPorts('', 0);
    }
  }, [initialPlaceholderValue]);

  const handleClose = () => {
    setOpen(false);
  };

  const { ref: scrollableDivRef } = useIntersectionObserver<HTMLDivElement>({
    threshold: 1,
    onChange: (entry) => {
      if (entry.isIntersecting) setOffset((prev) => prev + 10);
    },
  });

  const handleSelect = (selectedOption?: SelectedOptionI | null) => {
    if (!selectedOption?.value) {
      onSelect(fieldName, fieldName === 'port' ? '' : []);
      if (fieldName === 'port') return;
      setSearchState?.((prevState: SearchStateI) => ({
        term: {
          ...prevState.term,
          [fieldName]: '',
        },
        visibility: {
          ...prevState.visibility,
          [fieldName]: false,
        },
      }));
      if (fieldName === 'destinationPortKey') {
        onSelect('selectedDestinationPort', null);
      }
      if (fieldName === 'inPortKey') {
        onSelect('selectedInPort', null);
      }
    } else {
      const port = ports.ports.filter((port: PortCatalogData) => {
        return port.key?.toLowerCase() === selectedOption?.value?.toLowerCase();
      })?.[0];
      if (port) {
        onSelect(fieldName, fieldName === 'port' ? port.key : [port.key]);
        if (fieldName === 'port') {
          onSelect?.('isItemSelected', true);
        }
        setSearchState?.((prevState: SearchStateI) => ({
          term: {
            ...prevState.term,
            [fieldName]: port.name,
          },
          visibility: {
            ...prevState.visibility,
            [fieldName]: false,
          },
        }));
        if (fieldName === 'inPortKey') {
          onSelect('selectedInPort', port);
        }
        if (fieldName === 'destinationPortKey') {
          onSelect('selectedDestinationPort', port);
        }
      }
    }
    handleClose();
  };

  useEffect(() => {
    setOffset(0);
    catalogRequest.reset();
    if (debouncedValue) {
      setPorts({ ports: [] });
    }

    if (!catalogRequest.isUninitialized && open) {
      getPorts(debouncedValue, 0);
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!catalogRequest.isUninitialized && open) {
      getPorts(debouncedValue, offset);
    }
  }, [offset]);

  useEffect(() => {
    if (inputRef?.current) {
      setInputValue(inputRef.current.value);
    }
  }, [inputRef?.current?.value]);

  useEffect(() => {
    const options = ports?.ports.map((port) => ({
      value: port.key,
      placeholder: port.name,
      label: <InfoWrapper value={`${port.un} ${port.locode}`} label={port.name} />,
    }));
    setOptions(options);
  }, [ports]);

  useEffect(() => {
    if (portsData && portsData.results && portsData.results?.length > 0) {
      setPorts((prev) => {
        const prevPorts = prev.ports;
        const newPorts = portsData.results?.filter((port) => {
          return !prevPorts.some((prevPort) => prevPort.key === port.key);
        });
        return {
          ports: [...prevPorts, ...(newPorts || [])],
        };
      });
    }
  }, [portsData]);

  return (
    <AutoComplete
      className={styles.port_input}
      id={`autocomplete-${fieldName}`}
      placeholder={t('SelectPort')}
      initialValue={initialPlaceholderValue}
      staticSearch={false}
      inputRef={inputRef}
      divRef={scrollableDivRef}
      loading={catalogRequest.isLoading}
      options={options}
      isSuccess={catalogRequest.isSuccess}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onSelect={handleSelect}
      onChange={setInputValue}
      hasmore={portsData?.results?.length !== 0}
    />
  );
};
